<template>
    <v-app>
        <portal-sysbar :show-customer="false"></portal-sysbar>
        <div class="main-content">
            <portal-aside></portal-aside>
            <portal-title title="Locomotives / Series"></portal-title>
            <portal-breadcumb></portal-breadcumb>
            <div class="content">
                <v-tabs v-model="tab" background-color="transparent" class="mb-6">
                    <v-tab>
                        <v-icon left size="20">mdi-train-variant</v-icon>
                        Locomotives
                        <v-chip x-small class="ml-2" color="primary" text-color="white">{{ locomotives.length
                            }}</v-chip>
                    </v-tab>
                    <v-tab>
                        <v-icon left size="20">mdi-shape-outline</v-icon>
                        Séries
                        <v-chip x-small class="ml-2" color="primary" text-color="white">{{ series.length }}</v-chip>
                    </v-tab>
                </v-tabs>

                <v-tabs-items v-model="tab" class="transparent">
                    <!-- ============ Onglet Locomotives ============ -->
                    <v-tab-item>
                        <v-card outlined class="rounded-lg">
                            <div class="d-flex flex-wrap align-center pa-4" style="gap: 16px">
                                <v-text-field v-model="search" dense outlined hide-details clearable
                                    prepend-inner-icon="mdi-magnify" placeholder="Rechercher une locomotive…"
                                    style="max-width: 340px"></v-text-field>
                                <v-spacer></v-spacer>
                                <span class="text-caption grey--text text--darken-1">
                                    <v-icon size="14" class="mr-1">mdi-clock-outline</v-icon>
                                    Dernière synchro : {{ }}
                                </span>
                                <v-btn color="primary" depressed :loading="synchronisationEnCours"
                                    @click="synchroniser">
                                    <v-icon left>mdi-sync</v-icon>
                                    Synchroniser
                                </v-btn>
                            </div>

                            <v-divider></v-divider>

                            <v-data-table :headers="locomotiveHeader" :items="locomotives" :search="search"
                                :loading="locoLoading" loading-text="Chargement des locomotives…"
                                no-data-text="Aucune locomotive. Lancez une synchronisation."
                                no-results-text="Aucun résultat pour cette recherche."
                                :footer-props="{ itemsPerPageText: 'Lignes par page' }">
                                <template v-slot:item.loconum="{ item }">
                                    <div class="d-flex align-center py-2">
                                        <v-avatar size="36" class="mr-3">
                                            <v-icon size="20">mdi-train-variant</v-icon>
                                        </v-avatar>
                                        <span class="font-weight-medium">{{ item.loconum }}</span>
                                    </div>
                                </template>

                                <template v-slot:item.updatedAt="{ item }">
                                    <span class="text-body-2 grey--text text--darken-2">{{
                                        formatDate(item.updatedAt) }}</span>
                                </template>
                            </v-data-table>
                        </v-card>
                    </v-tab-item>

                    <!-- ============ Onglet Séries ============ -->
                    <v-tab-item>
                        <div class="d-flex align-center mb-4">
                            <p class="text-body-2 grey--text text--darken-1 mb-0">
                                {{ series.length }} série{{ series.length > 1 ? 's' : '' }} configurée{{ series.length >
                                    1 ? 's' : '' }}
                            </p>
                            <v-spacer></v-spacer>
                            <v-btn color="primary" depressed @click="openSerieDialog()">
                                <v-icon left>mdi-plus</v-icon>
                                Nouvelle série
                            </v-btn>
                        </div>

                        <v-row>
                            <v-col v-for="serie in series" :key="serie.id" cols="12" sm="6" md="4">
                                <v-card outlined class="rounded-lg overflow-hidden">
                                    <v-img :src="'data:image/png;base64,' + serie.image" height="150"></v-img>
                                    <v-card-title class="pb-1">{{ serie.locoserie }}</v-card-title>
                                    <v-card-text class="pb-2">
                                        <div class="d-flex align-center mb-2">
                                            <v-icon size="18" color="amber darken-2"
                                                class="mr-2">mdi-battery-high</v-icon>
                                            <span>Batterie max : <strong>{{ serie.batteryMax }}
                                                    kWh</strong></span>
                                        </div>
                                        <div class="d-flex align-center">
                                            <v-icon size="18" color="deep-orange" class="mr-2">mdi-fuel</v-icon>
                                            <span>Fioul max : <strong>{{ serie.fioulMax }} L</strong></span>
                                        </div>
                                    </v-card-text>
                                    <v-divider></v-divider>
                                    <v-card-actions>
                                        <v-btn text small color="primary" @click="openSerieDialog(serie)">
                                            <v-icon left small>mdi-pencil-outline</v-icon> Modifier
                                        </v-btn>
                                        <v-spacer></v-spacer>
                                        <v-btn icon small color="error" @click="confirmerSuppression(serie)">
                                            <v-icon small>mdi-delete-outline</v-icon>
                                        </v-btn>
                                    </v-card-actions>
                                </v-card>
                            </v-col>
                        </v-row>
                    </v-tab-item>
                </v-tabs-items>

                <!-- ============ Dialog création / édition de série ============ -->
                <v-dialog v-model="dialogSerie" max-width="520" persistent>
                    <v-card class="rounded-lg">
                        <v-card-title class="text-h6">
                            {{ editSerie ? 'Modifier la série' : 'Nouvelle série' }}
                        </v-card-title>

                        <v-card-text>
                            <v-form ref="formSerie" v-model="validForm">
                                <!-- Aperçu de la photo -->
                                <v-img v-if="imageHolder" :src="imageHolder" height="140" class="rounded-lg mb-4" />
                                <div v-else
                                    class="photo-placeholder rounded-lg d-flex align-center justify-center mb-4">
                                    <v-icon size="40" color="grey">mdi-image-outline</v-icon>
                                </div>

                                <v-file-input v-model="image" label="Photo de la série"
                                    prepend-icon="mdi-camera-outline" accept="image/*" outlined dense show-size />

                                <v-text-field v-model="formSerie.locoserie" label="Nom de la série" outlined dense
                                    prepend-inner-icon="mdi-shape-outline" :rules="[rules.requis]" />

                                <v-text-field v-model.number="formSerie.batteryMax"
                                    label="Niveau max de la batterie (V)" type="number" min="0" outlined dense
                                    prepend-inner-icon="mdi-battery-high" :rules="[rules.positif]" />

                                <v-text-field v-model.number="formSerie.fioulMax" label="Niveau max de fioul (L)"
                                    type="number" min="1" outlined dense prepend-inner-icon="mdi-fuel"
                                    :rules="[rules.positif]" />
                            </v-form>
                        </v-card-text>

                        <v-card-actions class="pa-4 pt-0">
                            <v-spacer />
                            <v-btn text @click="closeDialogSerie">Annuler</v-btn>
                            <v-btn color="primary" depressed :loading="saving" @click="saveSerie">
                                {{ serieEditee ? 'Enregistrer' : 'Créer' }}
                            </v-btn>
                        </v-card-actions>
                    </v-card>
                </v-dialog>

                <!-- ============ Dialog confirmation suppression ============ -->
                <v-dialog v-model="dialogSuppression" max-width="420">
                    <v-card class="rounded-lg">
                        <v-card-title class="text-h6">Supprimer la série ?</v-card-title>
                        <v-card-text>
                            La série <strong>{{ serieASupprimer && serieASupprimer.nom }}</strong> sera définitivement
                            supprimée.
                            <v-alert v-if="serieASupprimer && nbLocosDeSerie(serieASupprimer.id) > 0" type="warning"
                                dense outlined class="mt-3 mb-0">
                                {{ nbLocosDeSerie(serieASupprimer.id) }} locomotive(s) sont rattachées à cette série.
                            </v-alert>
                        </v-card-text>
                        <v-card-actions>
                            <v-spacer />
                            <v-btn text @click="dialogSuppression = false">Annuler</v-btn>
                            <v-btn color="error" depressed :loading="enregistrement" @click="supprimerSerie">
                                Supprimer
                            </v-btn>
                        </v-card-actions>
                    </v-card>
                </v-dialog>
                <!-- ============ Snackbar ============ -->

                <v-snackbar v-model="snackbar.visible" :color="snackbar.couleur" timeout="3500">
                    {{ snackbar.message }}
                </v-snackbar>
            </div>
        </div>
    </v-app>
</template>
<script>
import PortalSysbar from "@/components/PortalSysbar";
import PortalAside from "@/components/PortalAside";
import PortalTitle from "@/components/PortalTitle";
import PortalBreadcumb from "@/components/PortalBreadcumb";
import CoreService from "@/services/CoreService";


export default {
    components: {
        PortalSysbar,
        PortalAside,
        PortalTitle,
        PortalBreadcumb,
    },
    data() {
        return {
            tab: new URLSearchParams(location.search).get('tab') === '1' ? 1 : 0,
            search: '',
            locoLoading: false,
            synchronisationEnCours: false,
            locomotiveHeader: [
                { text: 'Locomotive', value: 'loconum' },
                { text: 'Série', value: 'serial', sortable: false },
                { text: 'Sous-série', value: 'subserial' },
                { text: 'Immatriculation', value: 'immatriculation', sortable: false },
                { text: 'Alimentation', value: 'alimentation', sortable: false },
                { text: 'Railfleetnum', value: 'railfleetnum', sortable: false },
                { text: 'Dernière synchro', value: 'updatedAt' },
            ],
            locomotives: [],
            series: [],
            snackbar: { visible: false, message: '', couleur: 'success' },
            dialogSerie: false,
            dialogSuppression: false,
            editSerie: null,
            serieASupprimer: null,
            validForm: false,
            saving: false,
            image: null,
            imageHolder: '',
            formSerie: { locoserie: '', batteryMax: null, fioulMax: null },
            rules: {
                requis: (v) => !!v || 'Champ requis',
                positif: (v) => v > 0 || 'La valeur doit être positive',
            },
        }
    },
    watch: {
        // Aperçu instantané de la photo choisie
        image(file) {
            if (this.imageHolder && this.imageHolder.startsWith('blob:')) {
                URL.revokeObjectURL(this.imageHolder);
            }
            this.imageHolder = file
                ? URL.createObjectURL(file)
                : (this.editSerie ? 'data:image/png;base64,' + this.editSerie.image : '');
        },
    },

    created() {
        this.loadSeries();

        this.loadLocomotives();
    },
    methods: {
        totalLocomotive() {
            return this.locomotives.length
        },
        async loadLocomotives() {
            this.locoLoading = true
            await CoreService.findAllLocomotives()
                .then((response) => {
                    this.locomotives = response.data
                })
            this.locoLoading = false
        },
        async loadSeries() {
            CoreService.findAllSeries()
                .then((response) => {
                    this.series = response.data
                })
        },
        formatDate(iso) {
            if (!iso) return '—';
            return new Intl.DateTimeFormat('fr-FR', { dateStyle: 'short', timeStyle: 'short' }).format(new Date(iso));
        },
        synchroniser() {
            this.synchronisationEnCours = true;
            setTimeout(() => {
                this.synchronisationEnCours = false;
                this.derniereSynchro = new Date().toISOString();
                this.snackbar = { visible: true, message: 'Synchronisation terminée avec succès', couleur: 'success' };
            }, 1500);
        },
        openSerieDialog(serie = null) {
            this.editSerie = serie;
            this.formSerie = serie
                ? { locoserie: serie.locoserie, batteryMax: serie.batteryMax, fioulMax: serie.fioulMax }
                : { locoserie: '', batteryMax: null, fioulMax: null };
            this.image = null;
            this.imageHolder = serie ? 'data:image/png;base64,' + serie.image : '';
            this.dialogSerie = true;
            this.$nextTick(() => this.$refs.formSerie && this.$refs.formSerie.resetValidation());
        },

        closeDialogSerie() {
            this.dialogSerie = false;
            this.serieEditee = null;
            this.image = null;
            this.imageHolder = '';
        },

        async saveSerie() {
            if (!this.$refs.formSerie.validate()) return;
            this.enregistrement = true;
            try {
                const formData = new FormData();
                formData.append(
                    'locoserie',
                    new Blob(
                        [JSON.stringify(this.formSerie)],
                        { type: 'application/json' }
                    )
                )

                if (this.image) {
                    formData.append('image', this.image)
                }

                CoreService.saveSerie(formData).then((response) => {
                    if (response.status == 200) {
                        this.notifier('Serie ajoutée avec succès', 'success');
                    }
                }).catch(() => {
                    this.notifier('Erreur lors de l\'enregistrement', 'error');
                })

                this.closeDialogSerie();
            } catch (e) {
                this.notifier('Erreur lors de l\'enregistrement', 'error');
            } finally {
                this.enregistrement = false;
            }
        },

        notifier(message, couleur) {
            this.snackbar = { visible: true, message, couleur };
        },

        confirmerSuppression(serie) {
            this.serieASupprimer = serie;
            this.dialogSuppression = true;
        },

        async supprimerSerie() {
            this.enregistrement = true;
            try {
                // TODO : await api.delete('/series/' + this.serieASupprimer.id)
                await new Promise((r) => setTimeout(r, 400));
                this.series = this.series.filter((s) => s.id !== this.serieASupprimer.id);
                this.notifier('Série supprimée', 'success');
                this.dialogSuppression = false;
            } catch (e) {
                this.notifier('Erreur lors de la suppression', 'error');
            } finally {
                this.enregistrement = false;
            }
        },

    },

}
</script>